/**
 * IndexedDB Wrapper for Offline-First Architecture
 * Stores wells, entries, messages, tasks, sync queue, and satellite images
 */

import type { DraftProductionEntry } from '../../types/draftEntry';
import type { 
  VoiceFieldTraining, 
  FieldVoicePreferences, 
  TenantVoiceModel 
} from '../../types/voiceML';

const DB_NAME = 'OilWellManager';
const DB_VERSION = 6; // Incremented for well assignment stores

// ═══════════════════════════════════════════════════════════════════════════════
// WELL ASSIGNMENT OFFLINE TYPES — Tasks #121-125
// ═══════════════════════════════════════════════════════════════════════════════

export interface CachedWellVisibilitySettings {
  companyId: string;
  mode: 'all' | 'assigned';
  notifyOnAssign: boolean;
  notifyOnUnassign: boolean;
  showWorkloadIndicators: boolean;
  allowPumperSelfAssign: boolean;
  cachedAt: number;
  lastSync: number;
}

export interface CachedTemporaryAssignment {
  id: string;
  companyId: string;
  coveringPumperId: string;
  coveringPumperName: string;
  originalPumperId?: string;
  originalPumperName?: string;
  wellIds: string[];
  reason: string;
  durationType: 'end_date' | 'reminder' | 'manual';
  startDate: string; // ISO string
  endDate?: string; // ISO string
  reminderDate?: string; // ISO string
  endAction: 'reassign' | 'unassign' | 'notify';
  status: 'active' | 'completed' | 'cancelled' | 'extended';
  cachedAt: number;
  lastSync: number;
  // Offline tracking
  createdOffline?: boolean;
  pendingSync?: boolean;
}

export interface CachedAssignmentHistory {
  id: string;
  wellId: string;
  companyId: string;
  action: string;
  pumperId?: string;
  pumperName?: string;
  previousPumperId?: string;
  previousPumperName?: string;
  method: 'manual' | 'bulk' | 'route' | 'auto' | 'coverage' | 'backup';
  reason?: string;
  timestamp: number;
  performedById: string;
  performedByName: string;
  cachedAt: number;
}

export type AssignmentSyncType = 
  | 'ASSIGN_WELL'
  | 'UNASSIGN_WELL'
  | 'BULK_ASSIGN'
  | 'CREATE_TEMP_ASSIGNMENT'
  | 'END_TEMP_ASSIGNMENT'
  | 'EXTEND_TEMP_ASSIGNMENT'
  | 'MAKE_PERMANENT'
  | 'ACTIVATE_BACKUP'
  | 'DEACTIVATE_BACKUP'
  | 'UPDATE_VISIBILITY_SETTINGS';

export interface SyncQueueItem {
  id: string;
  type: 'CREATE' | 'UPDATE' | 'DELETE' | 'UPDATE_DRAFT_FIELD' | 'FINALIZE_DRAFT' | 'SYNC_VOICE_TRAINING' | 'SYNC_VOICE_PREFERENCES' | 'SYNC_TENANT_MODEL' | AssignmentSyncType;
  entity: 'well' | 'entry' | 'message' | 'task' | 'note' | 'draft_entry' | 'voice_training' | 'voice_preferences' | 'tenant_model' | 'well_assignment' | 'temp_assignment' | 'visibility_settings';
  data: any;
  timestamp: number;
  retryCount: number;
  lastError?: string;
  status: 'pending' | 'syncing' | 'failed' | 'success';
  priority?: number; // 1=critical (temp assignments), 2=high (assignments), 3=normal, 10=low
  // Temporal metadata for assignment sync
  assignmentMeta?: {
    endDate?: string; // ISO string - for temporal validation
    requiresDateCheck?: boolean;
    originalTimestamp?: number;
  };
}

export interface CachedSatelliteImage {
  wellId: string;
  imageBlob: Blob;
  thumbnailBlob?: Blob;
  imageUrl: string;
  thumbnailUrl?: string;
  metadata: {
    zoom: number;
    centerLat: number;
    centerLng: number;
    hasAnnotations: boolean;
    createdAt: Date;
    createdBy: string;
  };
  cachedAt: number;
  lastAccessed: number;
  size: number; // bytes
}

export interface OfflineData {
  wells: any[];
  entries: any[];
  messages: any[];
  tasks: any[];
  notes: any[];
  syncQueue: SyncQueueItem[];
  satelliteImages: CachedSatelliteImage[];
  lastSync: number;
}

class IndexedDBService {
  private db: IDBDatabase | null = null;
  private readonly dbName = DB_NAME;
  private readonly version = DB_VERSION;

  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;

        // Wells store
        if (!db.objectStoreNames.contains('wells')) {
          const wellStore = db.createObjectStore('wells', { keyPath: 'id' });
          wellStore.createIndex('status', 'status', { unique: false });
          wellStore.createIndex('lastSync', 'lastSync', { unique: false });
        }

        // Entries store
        if (!db.objectStoreNames.contains('entries')) {
          const entryStore = db.createObjectStore('entries', { keyPath: 'id' });
          entryStore.createIndex('wellId', 'wellId', { unique: false });
          entryStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Messages store
        if (!db.objectStoreNames.contains('messages')) {
          const messageStore = db.createObjectStore('messages', { keyPath: 'id' });
          messageStore.createIndex('threadId', 'threadId', { unique: false });
          messageStore.createIndex('timestamp', 'timestamp', { unique: false });
          messageStore.createIndex('read', 'read', { unique: false });
        }

        // Tasks store
        if (!db.objectStoreNames.contains('tasks')) {
          const taskStore = db.createObjectStore('tasks', { keyPath: 'id' });
          taskStore.createIndex('assignedTo', 'assignedTo', { unique: false });
          taskStore.createIndex('status', 'status', { unique: false });
          taskStore.createIndex('priority', 'priority', { unique: false });
        }

        // Notes store
        if (!db.objectStoreNames.contains('notes')) {
          const noteStore = db.createObjectStore('notes', { keyPath: 'id' });
          noteStore.createIndex('wellId', 'wellId', { unique: false });
          noteStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Sync Queue store
        if (!db.objectStoreNames.contains('syncQueue')) {
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id' });
          syncStore.createIndex('status', 'status', { unique: false });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          syncStore.createIndex('retryCount', 'retryCount', { unique: false });
        }

        // Metadata store
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }

        // Voice Learning store (v3)
        if (!db.objectStoreNames.contains('voice_learning')) {
          const voiceStore = db.createObjectStore('voice_learning', { keyPath: 'id' });
          voiceStore.createIndex('embedding', 'embedding', { unique: false });
          voiceStore.createIndex('phrase', 'phrase', { unique: false });
        }

        // Satellite Images store (v2)
        if (oldVersion < 2 && !db.objectStoreNames.contains('satelliteImages')) {
          const satelliteStore = db.createObjectStore('satelliteImages', { keyPath: 'wellId' });
          satelliteStore.createIndex('cachedAt', 'cachedAt', { unique: false });
          satelliteStore.createIndex('lastAccessed', 'lastAccessed', { unique: false });
          satelliteStore.createIndex('size', 'size', { unique: false });
        }

        // Draft Entries store (v4)
        if (oldVersion < 4 && !db.objectStoreNames.contains('draft_entries')) {
          const draftStore = db.createObjectStore('draft_entries', { keyPath: 'id' });
          draftStore.createIndex('wellId', 'wellId', { unique: false });
          draftStore.createIndex('userId', 'userId', { unique: false });
          draftStore.createIndex('entryDate', 'entryDate', { unique: false });
          draftStore.createIndex('lastModifiedAt', 'lastModifiedAt', { unique: false });
          draftStore.createIndex('composite', ['wellId', 'userId', 'entryDate'], { unique: true });
        }

        // Voice ML stores (v5)
        if (oldVersion < 5) {
          // Field Training Data store
          if (!db.objectStoreNames.contains('field_training_data')) {
            const trainingStore = db.createObjectStore('field_training_data', { keyPath: 'id' });
            trainingStore.createIndex('companyId', 'companyId', { unique: false });
            trainingStore.createIndex('userId', 'userId', { unique: false });
            trainingStore.createIndex('fieldName', 'fieldName', { unique: false });
            trainingStore.createIndex('composite', ['companyId', 'userId', 'fieldName'], { unique: false });
            trainingStore.createIndex('confidence', 'confidence', { unique: false });
            trainingStore.createIndex('timestamp', 'timestamp', { unique: false });
            trainingStore.createIndex('wasCorrection', 'wasCorrection', { unique: false });
          }

          // Field Voice Preferences store
          if (!db.objectStoreNames.contains('field_voice_preferences')) {
            const prefsStore = db.createObjectStore('field_voice_preferences', { keyPath: 'id' });
            prefsStore.createIndex('companyId', 'companyId', { unique: false });
            prefsStore.createIndex('userField', ['userId', 'fieldName'], { unique: true });
            prefsStore.createIndex('accuracy', 'accuracy', { unique: false });
            prefsStore.createIndex('lastTrained', 'lastTrained', { unique: false });
          }

          // Tenant Voice Models store
          if (!db.objectStoreNames.contains('tenant_voice_models')) {
            const modelStore = db.createObjectStore('tenant_voice_models', { keyPath: 'companyId' });
            modelStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
            modelStore.createIndex('activeUsers', 'activeUsers', { unique: false });
          }
        }

        // ═══════════════════════════════════════════════════════════════════════
        // WELL ASSIGNMENT STORES (v6) — Tasks #121-122
        // ═══════════════════════════════════════════════════════════════════════
        if (oldVersion < 6) {
          // Task #121: Add assignment indexes to wells store
          // Note: We can't add indexes to existing stores in upgrade, but we track
          // assignment fields in the well objects themselves (primaryPumperId, 
          // assignedPumperIds[], backupPumperId, temporaryAssignment)

          // Task #122: Well Visibility Settings cache
          if (!db.objectStoreNames.contains('well_visibility_settings')) {
            const visibilityStore = db.createObjectStore('well_visibility_settings', { keyPath: 'companyId' });
            visibilityStore.createIndex('mode', 'mode', { unique: false });
            visibilityStore.createIndex('cachedAt', 'cachedAt', { unique: false });
          }

          // Temporary Assignments cache
          if (!db.objectStoreNames.contains('temporary_assignments')) {
            const tempStore = db.createObjectStore('temporary_assignments', { keyPath: 'id' });
            tempStore.createIndex('companyId', 'companyId', { unique: false });
            tempStore.createIndex('status', 'status', { unique: false });
            tempStore.createIndex('coveringPumperId', 'coveringPumperId', { unique: false });
            tempStore.createIndex('originalPumperId', 'originalPumperId', { unique: false });
            tempStore.createIndex('endDate', 'endDate', { unique: false });
            tempStore.createIndex('reminderDate', 'reminderDate', { unique: false });
            tempStore.createIndex('compound_status_end', ['status', 'endDate'], { unique: false });
            tempStore.createIndex('pendingSync', 'pendingSync', { unique: false });
          }

          // Assignment History cache
          if (!db.objectStoreNames.contains('assignment_history')) {
            const historyStore = db.createObjectStore('assignment_history', { keyPath: 'id' });
            historyStore.createIndex('wellId', 'wellId', { unique: false });
            historyStore.createIndex('companyId', 'companyId', { unique: false });
            historyStore.createIndex('timestamp', 'timestamp', { unique: false });
            historyStore.createIndex('pumperId', 'pumperId', { unique: false });
            historyStore.createIndex('compound_well_time', ['wellId', 'timestamp'], { unique: false });
          }
        }
      };
    });
  }

  private getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    const transaction = this.db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
  }

  isInitialized(): boolean {
    return this.db !== null;
  }

  // ============= WELLS =============
  async saveWells(wells: any[]): Promise<void> {
    const store = this.getStore('wells', 'readwrite');
    const promises = wells.map(well => {
      return new Promise<void>((resolve, reject) => {
        const request = store.put({ ...well, lastSync: Date.now() });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });
    await Promise.all(promises);
  }

  async getWells(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('wells');
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async getWell(id: string): Promise<any | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('wells');
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async updateWell(id: string, updates: any): Promise<void> {
    const well = await this.getWell(id);
    if (!well) throw new Error('Well not found');
    const store = this.getStore('wells', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put({ ...well, ...updates, lastSync: Date.now() });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============= ENTRIES =============
  async saveEntries(entries: any[]): Promise<void> {
    const store = this.getStore('entries', 'readwrite');
    const promises = entries.map(entry => {
      return new Promise<void>((resolve, reject) => {
        const request = store.put({ ...entry, lastSync: Date.now() });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });
    await Promise.all(promises);
  }

  async getEntries(wellId?: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('entries');
      
      if (wellId) {
        const index = store.index('wellId');
        const request = index.getAll(wellId);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      } else {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      }
    });
  }

  // ============= MESSAGES =============
  async saveMessages(messages: any[]): Promise<void> {
    const store = this.getStore('messages', 'readwrite');
    const promises = messages.map(message => {
      return new Promise<void>((resolve, reject) => {
        const request = store.put({ ...message, lastSync: Date.now() });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });
    await Promise.all(promises);
  }

  async getMessages(threadId?: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('messages');
      
      if (threadId) {
        const index = store.index('threadId');
        const request = index.getAll(threadId);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      } else {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      }
    });
  }

  async markMessageAsRead(messageId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('messages', 'readwrite');
      const getRequest = store.get(messageId);
      
      getRequest.onsuccess = () => {
        const message = getRequest.result;
        if (message) {
          message.read = true;
          const putRequest = store.put(message);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };
      
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // ============= TASKS =============
  async saveTasks(tasks: any[]): Promise<void> {
    const store = this.getStore('tasks', 'readwrite');
    const promises = tasks.map(task => {
      return new Promise<void>((resolve, reject) => {
        const request = store.put({ ...task, lastSync: Date.now() });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });
    await Promise.all(promises);
  }

  async getTasks(assignedTo?: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('tasks');
      
      if (assignedTo) {
        const index = store.index('assignedTo');
        const request = index.getAll(assignedTo);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      } else {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      }
    });
  }

  // ============= NOTES =============
  async saveNotes(notes: any[]): Promise<void> {
    const store = this.getStore('notes', 'readwrite');
    const promises = notes.map(note => {
      return new Promise<void>((resolve, reject) => {
        const request = store.put({ ...note, lastSync: Date.now() });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });
    await Promise.all(promises);
  }

  async getNotes(wellId?: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('notes');
      
      if (wellId) {
        const index = store.index('wellId');
        const request = index.getAll(wellId);
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      } else {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      }
    });
  }

  // ============= SYNC QUEUE =============
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'retryCount' | 'status'>): Promise<string> {
    const queueItem: SyncQueueItem = {
      ...item,
      id: `sync-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
    };

    return new Promise((resolve, reject) => {
      const store = this.getStore('syncQueue', 'readwrite');
      const request = store.add(queueItem);
      request.onsuccess = () => resolve(queueItem.id);
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('syncQueue');
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingSyncItems(): Promise<SyncQueueItem[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('syncQueue');
      const index = store.index('status');
      const request = index.getAll('pending');
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async updateSyncItem(id: string, updates: Partial<SyncQueueItem>): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('syncQueue', 'readwrite');
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (item) {
          const updatedItem = { ...item, ...updates };
          const putRequest = store.put(updatedItem);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          reject(new Error('Sync item not found'));
        }
      };
      
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async removeSyncItem(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('syncQueue', 'readwrite');
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearSuccessfulSyncItems(): Promise<void> {
    const items = await this.getSyncQueue();
    const successfulItems = items.filter(item => item.status === 'success');
    
    const promises = successfulItems.map(item => this.removeSyncItem(item.id));
    await Promise.all(promises);
  }

  // ============= METADATA =============
  async setMetadata(key: string, value: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('metadata', 'readwrite');
      const request = store.put({ key, value, timestamp: Date.now() });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getMetadata(key: string): Promise<any | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('metadata');
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => reject(request.error);
    });
  }

  // ============= UTILITY =============
  async clearAllData(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const storeNames = ['wells', 'entries', 'messages', 'tasks', 'notes', 'syncQueue', 'metadata'];
    const promises = storeNames.map(storeName => {
      return new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });
    
    await Promise.all(promises);
  }

  async getStorageEstimate(): Promise<{ usage: number; quota: number; percentage: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentage = quota > 0 ? (usage / quota) * 100 : 0;
      
      return { usage, quota, percentage };
    }
    
    return { usage: 0, quota: 0, percentage: 0 };
  }

  // ============= SATELLITE IMAGES =============
  async cacheSatelliteImage(
    wellId: string,
    imageUrl: string,
    thumbnailUrl: string | undefined,
    metadata: CachedSatelliteImage['metadata']
  ): Promise<void> {
    try {
      // Fetch image as blob
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) throw new Error('Failed to fetch satellite image');
      const imageBlob = await imageResponse.blob();

      // Fetch thumbnail if provided
      let thumbnailBlob: Blob | undefined;
      if (thumbnailUrl) {
        const thumbResponse = await fetch(thumbnailUrl);
        if (thumbResponse.ok) {
          thumbnailBlob = await thumbResponse.blob();
        }
      }

      const cachedImage: CachedSatelliteImage = {
        wellId,
        imageBlob,
        thumbnailBlob,
        imageUrl,
        thumbnailUrl,
        metadata,
        cachedAt: Date.now(),
        lastAccessed: Date.now(),
        size: imageBlob.size + (thumbnailBlob?.size || 0),
      };

      return new Promise((resolve, reject) => {
        const store = this.getStore('satelliteImages', 'readwrite');
        const request = store.put(cachedImage);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to cache satellite image:', error);
      throw error;
    }
  }

  async getCachedSatelliteImage(wellId: string): Promise<CachedSatelliteImage | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('satelliteImages', 'readwrite');
      const request = store.get(wellId);
      
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          // Update last accessed time
          result.lastAccessed = Date.now();
          store.put(result);
          resolve(result);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async removeCachedSatelliteImage(wellId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('satelliteImages', 'readwrite');
      const request = store.delete(wellId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAllCachedSatelliteImages(): Promise<CachedSatelliteImage[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('satelliteImages');
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async getSatelliteCacheSize(): Promise<number> {
    const images = await this.getAllCachedSatelliteImages();
    return images.reduce((total, img) => total + img.size, 0);
  }

  async pruneSatelliteCache(maxSizeBytes: number = 50 * 1024 * 1024): Promise<void> {
    // Max 50MB by default
    const images = await this.getAllCachedSatelliteImages();
    const totalSize = images.reduce((total, img) => total + img.size, 0);

    if (totalSize <= maxSizeBytes) return;

    // Sort by last accessed (oldest first)
    images.sort((a, b) => a.lastAccessed - b.lastAccessed);

    let sizeToRemove = totalSize - maxSizeBytes;
    const promises: Promise<void>[] = [];

    for (const image of images) {
      if (sizeToRemove <= 0) break;
      promises.push(this.removeCachedSatelliteImage(image.wellId));
      sizeToRemove -= image.size;
    }

    await Promise.all(promises);
  }

  async clearSatelliteCache(): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('satelliteImages', 'readwrite');
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============= DRAFT ENTRIES =============
  async saveDraftEntry(draft: DraftProductionEntry): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('draft_entries', 'readwrite');
      const request = store.put(draft);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getDraftEntry(
    wellId: string,
    userId: string,
    entryDate: string
  ): Promise<DraftProductionEntry | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('draft_entries');
      const index = store.index('composite');
      const request = index.get([wellId, userId, entryDate]);
      
      request.onsuccess = () => {
        const result = request.result || null;
        if (result) {
        } else {
        }
        resolve(result);
      };
      
      request.onerror = () => {
        console.error('❌ Error querying IndexedDB for draft:', request.error);
        reject(request.error);
      };
    });
  }

  async getDraftEntryById(draftId: string): Promise<DraftProductionEntry | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('draft_entries');
      const request = store.get(draftId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async updateDraftField(
    draftId: string,
    fieldName: string,
    fieldData: { value: any; savedAt: string; isSaved: boolean; hasError: boolean; errorMessage?: string }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('draft_entries', 'readwrite');
      const getRequest = store.get(draftId);

      getRequest.onsuccess = () => {
        const draft = getRequest.result;
        if (!draft) {
          console.error(`❌ Draft not found for ID: ${draftId}`);
          reject(new Error(`Draft not found: ${draftId}`));
          return;
        }
        

        // Update field state
        draft.fields[fieldName] = fieldData;

        // Update the actual data field
        // Map frontend field names to database field names
        const fieldMap: Record<string, string> = {
          // Original fields
          gasVolume: 'gasVolumeMcf',
          casingPressure: 'casingPressurePsi',
          tubingPressure: 'tubingPressurePsi',
          waterGauge: 'gauges',
          oilGauge: 'gauges',
          totalSales: 'totalSalesBbl',
          maintenanceCost: 'maintenanceCost',
          notes: 'notes',
          // New Gas Section fields
          yesterdaysSales: 'yesterdaysSalesMcf',
          gasFlowRate: 'gasFlowRateMcfDay',
          staticPressure: 'staticPressurePsi',
          differentialPressure: 'differentialPressurePsi',
          totalAccumulated: 'totalAccumulatedMcf',
        };

        const actualFieldName = fieldMap[fieldName] || fieldName;
        
        if (fieldName === 'waterGauge' || fieldName === 'oilGauge') {
          if (!draft.gauges) draft.gauges = {};
          const gaugeType = fieldName === 'waterGauge' ? 'water' : 'oil';
          draft.gauges[gaugeType] = fieldData.value;
        } else if (fieldName === 'tankReadings') {
          // Handle tank readings array - store directly
          (draft as any).tankReadings = fieldData.value;
        } else {
          draft[actualFieldName as keyof DraftProductionEntry] = fieldData.value;
        }

        draft.lastModifiedAt = new Date().toISOString();

        const putRequest = store.put(draft);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async deleteDraftEntry(draftId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('draft_entries', 'readwrite');
      const request = store.delete(draftId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAllDraftEntries(userId: string): Promise<DraftProductionEntry[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('draft_entries');
      const index = store.index('userId');
      const request = index.getAll(userId);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async getDraftEntriesForWell(wellId: string): Promise<DraftProductionEntry[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('draft_entries');
      const index = store.index('wellId');
      const request = index.getAll(wellId);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================
  // VOICE ML METHODS
  // ============================================

  /**
   * Save field training data
   */
  async saveFieldTraining(training: VoiceFieldTraining): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('field_training_data', 'readwrite');
      const request = store.put(training);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get field training data for a user/field combination
   */
  async getFieldTrainingData(
    userId: string,
    fieldName: string,
    limit?: number
  ): Promise<VoiceFieldTraining[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('field_training_data');
      const index = store.index('composite');
      const results: VoiceFieldTraining[] = [];
      
      // Get all matching entries
      const request = store.openCursor();
      let count = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const data = cursor.value as VoiceFieldTraining;
          if (data.userId === userId && data.fieldName === fieldName) {
            results.push(data);
            count++;
            if (limit && count >= limit) {
              resolve(results.sort((a, b) => b.timestamp - a.timestamp));
              return;
            }
          }
          cursor.continue();
        } else {
          resolve(results.sort((a, b) => b.timestamp - a.timestamp));
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all training data for a tenant (for cross-user learning)
   */
  async getTenantTrainingData(
    companyId: string,
    fieldName?: string
  ): Promise<VoiceFieldTraining[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('field_training_data');
      const index = store.index('companyId');
      const request = index.getAll(companyId);
      
      request.onsuccess = () => {
        let results = request.result || [];
        if (fieldName) {
          results = results.filter((t: VoiceFieldTraining) => t.fieldName === fieldName);
        }
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save field voice preferences
   */
  async saveFieldPreferences(prefs: FieldVoicePreferences): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('field_voice_preferences', 'readwrite');
      const request = store.put(prefs);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get field voice preferences for a user/field combination
   */
  async getFieldPreferences(
    userId: string,
    fieldName: string
  ): Promise<FieldVoicePreferences | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('field_voice_preferences');
      const index = store.index('userField');
      const request = index.get([userId, fieldName]);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all preferences for a user
   */
  async getAllUserPreferences(userId: string): Promise<FieldVoicePreferences[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('field_voice_preferences');
      const results: FieldVoicePreferences[] = [];
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const data = cursor.value as FieldVoicePreferences;
          if (data.userId === userId) {
            results.push(data);
          }
          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save tenant voice model
   */
  async saveTenantModel(model: TenantVoiceModel): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('tenant_voice_models', 'readwrite');
      const request = store.put(model);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get tenant voice model
   */
  async getTenantModel(companyId: string): Promise<TenantVoiceModel | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('tenant_voice_models');
      const request = store.get(companyId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete old training data (privacy/cleanup)
   */
  async deleteOldTrainingData(beforeTimestamp: number): Promise<number> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('field_training_data', 'readwrite');
      const index = store.index('timestamp');
      const range = IDBKeyRange.upperBound(beforeTimestamp);
      const request = index.openCursor(range);
      let deletedCount = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // WELL ASSIGNMENT OFFLINE METHODS — Tasks #121-125
  // ═══════════════════════════════════════════════════════════════════════════════

  // ============= WELL VISIBILITY SETTINGS (Task #122) =============

  /**
   * Save well visibility settings to cache
   */
  async saveWellVisibilitySettings(settings: CachedWellVisibilitySettings): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('well_visibility_settings', 'readwrite');
      const request = store.put({
        ...settings,
        cachedAt: Date.now(),
        lastSync: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get cached well visibility settings for a company
   */
  async getWellVisibilitySettings(companyId: string): Promise<CachedWellVisibilitySettings | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('well_visibility_settings');
      const request = store.get(companyId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  // ============= TEMPORARY ASSIGNMENTS (Task #124) =============

  /**
   * Save temporary assignment to cache
   */
  async saveTemporaryAssignment(assignment: CachedTemporaryAssignment): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('temporary_assignments', 'readwrite');
      const request = store.put({
        ...assignment,
        cachedAt: Date.now(),
        lastSync: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save multiple temporary assignments
   */
  async saveTemporaryAssignments(assignments: CachedTemporaryAssignment[]): Promise<void> {
    const store = this.getStore('temporary_assignments', 'readwrite');
    const promises = assignments.map(assignment => {
      return new Promise<void>((resolve, reject) => {
        const request = store.put({
          ...assignment,
          cachedAt: Date.now(),
          lastSync: Date.now(),
        });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });
    await Promise.all(promises);
  }

  /**
   * Get temporary assignment by ID
   */
  async getTemporaryAssignment(id: string): Promise<CachedTemporaryAssignment | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('temporary_assignments');
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all temporary assignments for a company
   */
  async getTemporaryAssignments(companyId: string, status?: string): Promise<CachedTemporaryAssignment[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('temporary_assignments');
      const index = store.index('companyId');
      const request = index.getAll(companyId);
      
      request.onsuccess = () => {
        let results = request.result || [];
        if (status) {
          results = results.filter((a: CachedTemporaryAssignment) => a.status === status);
        }
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get active temporary assignments for a pumper
   */
  async getActiveCoveragesForPumper(pumperId: string): Promise<CachedTemporaryAssignment[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('temporary_assignments');
      const index = store.index('coveringPumperId');
      const request = index.getAll(pumperId);
      
      request.onsuccess = () => {
        const results = (request.result || []).filter(
          (a: CachedTemporaryAssignment) => a.status === 'active'
        );
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get pending sync temporary assignments (created offline)
   */
  async getPendingSyncAssignments(): Promise<CachedTemporaryAssignment[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('temporary_assignments');
      const index = store.index('pendingSync');
      const request = index.getAll(true);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Mark temporary assignment as synced
   */
  async markTemporaryAssignmentSynced(id: string): Promise<void> {
    const assignment = await this.getTemporaryAssignment(id);
    if (!assignment) return;
    
    return new Promise((resolve, reject) => {
      const store = this.getStore('temporary_assignments', 'readwrite');
      const request = store.put({
        ...assignment,
        pendingSync: false,
        createdOffline: false,
        lastSync: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============= ASSIGNMENT HISTORY =============

  /**
   * Save assignment history entry
   */
  async saveAssignmentHistory(entry: CachedAssignmentHistory): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('assignment_history', 'readwrite');
      const request = store.put({
        ...entry,
        cachedAt: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save multiple history entries
   */
  async saveAssignmentHistoryBatch(entries: CachedAssignmentHistory[]): Promise<void> {
    const store = this.getStore('assignment_history', 'readwrite');
    const promises = entries.map(entry => {
      return new Promise<void>((resolve, reject) => {
        const request = store.put({ ...entry, cachedAt: Date.now() });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });
    await Promise.all(promises);
  }

  /**
   * Get assignment history for a well
   */
  async getAssignmentHistory(wellId: string, limit?: number): Promise<CachedAssignmentHistory[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('assignment_history');
      const index = store.index('wellId');
      const request = index.getAll(wellId);
      
      request.onsuccess = () => {
        let results = (request.result || []).sort(
          (a: CachedAssignmentHistory, b: CachedAssignmentHistory) => b.timestamp - a.timestamp
        );
        if (limit) {
          results = results.slice(0, limit);
        }
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============= ASSIGNMENT SYNC QUEUE (Tasks #123-124) =============

  /**
   * Add assignment change to sync queue (priority 2)
   */
  async queueAssignmentSync(
    type: AssignmentSyncType,
    data: any,
    assignmentMeta?: SyncQueueItem['assignmentMeta']
  ): Promise<string> {
    // Determine priority based on type
    let priority = 3; // Default normal
    if (type === 'CREATE_TEMP_ASSIGNMENT' || type === 'END_TEMP_ASSIGNMENT') {
      priority = 2; // High priority - critical for coverage
    } else if (type === 'ASSIGN_WELL' || type === 'UNASSIGN_WELL' || type === 'BULK_ASSIGN') {
      priority = 3; // Normal
    } else if (type === 'UPDATE_VISIBILITY_SETTINGS') {
      priority = 5; // Lower priority - settings can wait
    }

    const queueItem: SyncQueueItem = {
      id: `sync-assignment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      entity: type.includes('TEMP') ? 'temp_assignment' : 
              type.includes('VISIBILITY') ? 'visibility_settings' : 'well_assignment',
      data,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
      priority,
      assignmentMeta,
    };

    return new Promise((resolve, reject) => {
      const store = this.getStore('syncQueue', 'readwrite');
      const request = store.add(queueItem);
      request.onsuccess = () => resolve(queueItem.id);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get assignment-related sync items sorted by priority
   */
  async getAssignmentSyncQueue(): Promise<SyncQueueItem[]> {
    const allItems = await this.getSyncQueue();
    return allItems
      .filter(item => 
        item.entity === 'well_assignment' || 
        item.entity === 'temp_assignment' || 
        item.entity === 'visibility_settings'
      )
      .sort((a, b) => (a.priority || 10) - (b.priority || 10));
  }

  // ============= TEMPORAL VALIDATION (Task #125) =============

  /**
   * Validate and process expired coverages on reconnect
   * Returns list of assignments that need server reconciliation
   */
  async validateTemporalAssignments(): Promise<{
    expired: CachedTemporaryAssignment[];
    endingToday: CachedTemporaryAssignment[];
    needsReconciliation: boolean;
  }> {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const allAssignments = await new Promise<CachedTemporaryAssignment[]>((resolve, reject) => {
      const store = this.getStore('temporary_assignments');
      const index = store.index('status');
      const request = index.getAll('active');
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
    
    const expired: CachedTemporaryAssignment[] = [];
    const endingToday: CachedTemporaryAssignment[] = [];
    
    for (const assignment of allAssignments) {
      if (!assignment.endDate) continue;
      
      const endDate = new Date(assignment.endDate);
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      
      // Check if expired (end date passed)
      if (endDateOnly.getTime() < today.getTime()) {
        expired.push(assignment);
      }
      // Check if ending today
      else if (endDateOnly.getTime() === today.getTime()) {
        endingToday.push(assignment);
      }
    }
    
    // Mark expired assignments in local cache
    for (const exp of expired) {
      await this.saveTemporaryAssignment({
        ...exp,
        status: 'completed',
        pendingSync: true, // Needs server reconciliation
      });
    }
    
    return {
      expired,
      endingToday,
      needsReconciliation: expired.length > 0,
    };
  }

  /**
   * Check sync queue for stale assignment operations
   * (operations that reference dates that have passed)
   */
  async validateSyncQueueDates(): Promise<{
    staleItems: SyncQueueItem[];
    validItems: SyncQueueItem[];
  }> {
    const now = Date.now();
    const items = await this.getAssignmentSyncQueue();
    
    const staleItems: SyncQueueItem[] = [];
    const validItems: SyncQueueItem[] = [];
    
    for (const item of items) {
      if (item.status !== 'pending') {
        validItems.push(item);
        continue;
      }
      
      // Check if item has temporal metadata
      if (item.assignmentMeta?.requiresDateCheck && item.assignmentMeta.endDate) {
        const endDate = new Date(item.assignmentMeta.endDate);
        if (endDate.getTime() < now) {
          // This operation references a date that has passed
          staleItems.push(item);
          // Mark as failed with reason
          await this.updateSyncItem(item.id, {
            status: 'failed',
            lastError: 'Operation references a date that has passed while offline. Server reconciliation needed.',
          });
          continue;
        }
      }
      
      validItems.push(item);
    }
    
    return { staleItems, validItems };
  }

  /**
   * Full offline-to-online reconciliation for assignments
   * Call this when coming back online
   */
  async reconcileAssignmentsOnReconnect(): Promise<{
    expiredCoverages: number;
    staleSyncItems: number;
    pendingSyncItems: number;
  }> {
    // 1. Validate temporal assignments (expired coverages)
    const temporalResult = await this.validateTemporalAssignments();
    
    // 2. Validate sync queue dates
    const syncResult = await this.validateSyncQueueDates();
    
    // 3. Get remaining valid pending items
    const pendingItems = syncResult.validItems.filter(i => i.status === 'pending');
    
    console.log('[IndexedDB] Assignment reconciliation complete:', {
      expiredCoverages: temporalResult.expired.length,
      endingToday: temporalResult.endingToday.length,
      staleSyncItems: syncResult.staleItems.length,
      pendingSyncItems: pendingItems.length,
    });
    
    return {
      expiredCoverages: temporalResult.expired.length,
      staleSyncItems: syncResult.staleItems.length,
      pendingSyncItems: pendingItems.length,
    };
  }
}

// Singleton instance
export const indexedDBService = new IndexedDBService();